import React, { useEffect, useState, useRef } from 'react'
import {
  Input, UncontrolledTooltip, Label,
  Card, CardHeader, CardBody, CardFooter
} from 'reactstrap'
import {
  IoMdTrash,
  IoIosAddCircle, IoMdArrowDroprightCircle,
  IoMdClose
} from 'react-icons/io'
import Moment from 'moment'

import Lang from 'Lang/General'
import UploadModal from './UploadModal'
import Message from './Message'
import './index.scss'

const DialogueBox = props => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [searchText, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const dialogBoxRef = useRef(null)
  const messageInputRef = useRef(null)

  useEffect(() => {
    if (!isUploadModalOpen && props.selectedDialogue.id > 0) {
      setTimeout(() => {
        window.document.getElementById('message-input').focus()
      }, 500)
    }
  }, [isUploadModalOpen])

  useEffect(() => {
    if (props.scrollIntoInternal && !props.isInternalDialogue) {
      const element = document.getElementById(props.scrollIntoInternal);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: 'center', inline: 'center' });
        props.setScroll(null)
      }
    }
  }, [props.scrollIntoInternal])

  useEffect(() => {
    if (messageInputRef.current) {
      messageInputRef.current.style.height = 'auto'
      messageInputRef.current.style.height = `${messageInputRef.current.scrollHeight}px`
    }
  }, [props.internalDialogueMessage, props.dialogueMessage])

  // Search functionality
  useEffect(() => {
    if (searchText.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = [];
    if (!props.isInternalDialogue && props.selectedDialogue.dialogue_messages?.length > 0) {
      props.selectedDialogue.dialogue_messages.forEach(dateMessages => {
        dateMessages.messages.forEach(message => {
          if (message.content && message.content.toLowerCase().includes(searchText.toLowerCase())) {
            results.push({
              id: message.id,
              content: message.content,
              created_at: message.created_at,
              date: dateMessages.date,
              time: Moment(message.created_at).format('h:mm A')
            });
          }
        });
      });
    }

    setSearchResults(results);
    setShowSearchResults(true);
  }, [searchText, props.selectedDialogue.dialogue_messages]);

  // Add this useEffect to clear search when dialogue changes
  useEffect(() => {
    setSearch('');
    setSearchResults([]);
    setShowSearchResults(false);
  }, [props.selectedDialogue.id]); // Reset search when dialogue changes

  const scrollToMessage = (messageId) => {
    const element = document.getElementById(`dialogue-message-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: 'center' });
      // Highlight the message temporarily
      element.style.backgroundColor = 'rgba(0, 132, 255, 0.1)';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 2000);

      // Dismiss sidebar after selecting a result
      setSearch('');
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  let tempMsg = props.isInternalDialogue ? props.internalDialogueMessage : props.dialogueMessage

  return props.selectedDialogue.id > 0 ? (
    <>
      {
        props.dialogueSetting.id > 0 && (
          <div
            style={{
              height: props.isInternalDialogue ? 'calc(45vh - 15px)' : "76vh",
              position: 'relative' // Add this to handle absolute positioning of search results
            }}
            className='d-flex flex-column w-100' >
            { props.isInternalDialogue && <Label className='mt-3' >{ Lang['INTERNAL_NOTES'][props.selectedLanguage] }</Label>}
            {/* Search Sidebar with animation */}
            {showSearchResults && !props.isInternalDialogue && (
              <div style={{
                position: 'absolute',
                top: '50px',
                right: '0',
                width: '300px',
                height: 'calc(100% - 50px)',
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f8f9fa',
                zIndex: 1000,
                boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
                animation: 'slideIn 0.3s ease-out',
              }}>
                <div style={{
                  padding: '10px 15px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#f0f2f5'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        marginRight: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={() => {
                        setSearch('');
                        setSearchResults([]);
                        setShowSearchResults(false);
                      }}
                    >
                      <IoMdClose size={20} />
                    </span>
                    <span>
                      {searchResults.length > 0
                        ? `${Lang['SEARCH_RESULTS'][props.selectedLanguage]} (${searchResults.length})`
                        : `${Lang['SEARCH_RESULTS'][props.selectedLanguage]}`}
                    </span>
                  </div>
                </div>
                <div style={{
                  overflowY: 'auto',
                  flex: 1,
                  padding: '0',
                  backgroundColor: '#fff'
                }}>
                  {searchResults.length > 0 ? (
                    searchResults.map((result, index) => (
                      <div
                        key={`${result.id}-${index}`}
                        onClick={() => scrollToMessage(result.id)}
                        style={{
                          padding: '12px 15px',
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          backgroundColor: '#fff',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                      >
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          color: '#555',
                          marginBottom: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span>{result.date}</span>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: 'normal'
                          }}>
                            {result.time}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: '#333',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {result.content}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '30px 15px',
                      color: '#666',
                      textAlign: 'center',
                      height: '100%'
                    }}>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                        No results found
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        No messages match "{searchText}"
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Main Chat Card */}
            <Card className='w-100 h-100'>
              {
                !props.isInternalDialogue && (
                  <CardHeader
                    style={{ textTransform: "none" }}
                    className='d-flex flex-row align-items-center p-2 py-3' >
                    <div style={{ position: 'relative', width: '50%', marginRight: 15 }}>
                      <Input
                        value={searchText}
                        onChange={e => setSearch(e.target.value)}
                        className="mr-2"
                        placeholder={Lang['SEARCH_DIALOGUE_MSG'][props.selectedLanguage]}
                        style={{ width: '100%' }}
                      />
                      {searchText && (
                        <IoMdClose
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer'
                          }}
                          onClick={() => setSearch('')}
                        />
                      )}
                    </div>
                    <span>{Lang['DIALOGUE_ID'][props.selectedLanguage]}:
                      <span
                        id='dialogue-id'
                        className='ml-1'
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          navigator.clipboard.writeText(props.selectedDialogue.encrypted_id)
                          props.requestSuccess(Lang['DIALOGUE_ID_COPIED'][props.selectedLanguage])
                        }} >
                        {props.selectedDialogue.encrypted_id.substring(0, 10)}...
                      </span>
                      <UncontrolledTooltip
                        placement="top"
                        target='dialogue-id' >
                        {Lang['COPY_DIALOGUE_ID'][props.selectedLanguage]}
                      </UncontrolledTooltip>
                    </span>
                  </CardHeader>
                )
              }
              <CardBody
                className='px-2'
                id={!props.isInternalDialogue ? 'dialogue-container' : 'internal-dialogue-container'}
                style={{ overflowY: "scroll" }}
                ref={dialogBoxRef}
              >
                {
                  (!props.isInternalDialogue &&
                    props.selectedDialogue.dialogue_messages?.length > 0) && (
                    <div
                      id='message-container'
                      className='d-flex flex-column' >
                      {
                        props.selectedDialogue.dialogue_messages.map(dateMessages => ((
                          <>
                            <span className='text-center my-1' >
                              {dateMessages.date}
                            </span>
                            {
                              dateMessages.messages.map(message => <Message {...props} message={message} searchActive={showSearchResults} />)
                            }
                          </>
                        )
                        ))
                      }
                    </div>
                  )
                }
                {
                  (props.isInternalDialogue &&
                    props.selectedDialogue.internal_messages?.length > 0) && (
                    <>
                      {
                        props.selectedDialogue.internal_messages.map(message => (
                          <div
                            id='internal-message-container'
                            className='d-flex rounded mb-3 shadow'
                            style={{ cursor: 'pointer', background: '#dc3545', color: 'white' }}
                            onClick={() => {
                              props.setScroll(`dialogue-message-${message.id}`)
                            }}
                          >
                            <>
                              <Message {...props} message={message} searchActive={showSearchResults} />
                            </>
                          </div>
                        ))
                      }
                    </>
                  )
                }

              </CardBody>
              <CardFooter className='p-2 d-flex flex-column' >
                <div className='d-flex flex-row align-items-center w-100' >
                  <button
                    style={{
                      border: "none",
                      background: "none",
                      padding: "0",
                      fontSize: "200%"
                    }}
                    className='mr-2'
                    onClick={() => setIsUploadModalOpen(true)}>
                    <IoIosAddCircle />
                  </button>
                  <Input
                    id={props.isInternalDialogue ? 'internal-message-input' : 'message-input'}
                    innerRef={messageInputRef}
                    rows='1'
                    type='textarea'
                    className='mr-2'
                    autoComplete='off'
                    value={tempMsg.content}
                    style={{ maxHeight: "30vh" }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        props.createDialogueMessage(props.isInternalDialogue)
                      }
                    }}
                    onChange={e => props.onChangeDialogsHOC(
                      props.isInternalDialogue ? 'internalDialogueMessage' : 'dialogueMessage',
                      {
                        ...tempMsg,
                        content: e.target.value
                      }
                    )}
                  />
                  <button
                    style={{
                      border: "none",
                      background: "none",
                      padding: "0",
                      fontSize: "200%",
                    }}
                    onClick={() => props.createDialogueMessage(props.isInternalDialogue)}>
                    <IoMdArrowDroprightCircle />
                  </button>
                </div>
                {
                  tempMsg.dialogue_attachments.length > 0 && (
                    <div className='d-flex flex-column mt-2 w-100' >
                      {
                        tempMsg.dialogue_attachments.map((attachment, index) => (
                          <Card className='mb-1' >
                            <CardBody className='p-2 d-flex flex-row align-items-center justify-content-between' >
                              <span>{attachment.file.filename}</span>
                              <button
                                style={{
                                  border: "none",
                                  background: "none",
                                  padding: "0",
                                  fontSize: "200%",
                                  lineHeight: "1"
                                }}
                                className='mr-2'
                                onClick={() => {
                                  props.onChangeDialogsHOC(
                                    props.isInternalDialogue ? 'internalDialogueMessage' : 'dialogueMessage',
                                    {
                                      ...tempMsg,
                                      dialogue_attachments: tempMsg.dialogue_attachments.filter(
                                        (_, itemIndex) => itemIndex !== index
                                      )
                                    }
                                  )
                                }}>
                                <IoMdTrash />
                              </button>
                            </CardBody>
                          </Card>
                        ))
                      }
                    </div>
                  )
                }
              </CardFooter>
            </Card>
          </div>
        )
      }
      <UploadModal
        {...props}
        isUploadModalOpen={isUploadModalOpen}
        setIsUploadModalOpen={setIsUploadModalOpen}
      />
    </>
  ) : (
    <></>
  )
}

export default DialogueBox